import * as React from 'react'

import { Box, Hint, Skeleton } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { observer } from 'mobx-react'

import EmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import filterFields from 'modules/shift_management/components/ShiftManagement/Content/_filters/filterFields'
import { Separator } from 'modules/shift_management/components/ShiftManagement/Separator'
import {
  SHIFT_CONFLICTS,
  SHIFT_WITHOUT_CONFLICTS,
} from 'modules/shift_management/hooks/reducers/filter'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'

import { ViewOptionsSectionButton } from '../ShiftActionsBar/ViewOptionsSectionButton'
import FilterPopover from './FilterPopover'
import LocationSelector from './LocationSelector'
import ViewSelectorOld from './ViewSelector/indexOld'
import * as styles from './index.vanilla.css'

interface Props {
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  disabled?: boolean
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: () => void
  onCancel: () => void
  onResetFilters: () => void
  canManageShifts: boolean
  hasNoneShiftManagementPlanning: boolean
}

const CommandsFilterBar = ({
  filters,
  logicOperator,
  disabled,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
  onResetFilters,
  canManageShifts,
  hasNoneShiftManagementPlanning,
}: Props) => {
  const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false)
  const [{ view }] = useSchedule()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasMonthlyView = hasFeatureFlag('shift_management_monthly_view')
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const [hasSeenNewEntryFlowHints] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHasSeenNewEntryFlowHints,
    false
  )

  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading } = useAsyncData(async () => {
    const allFields = await filterFields(statusOptions)

    const fields = newEntryFlowEnabled
      ? allFields.filter((field) => field.value !== 'location')
      : allFields

    setFields(fields)
  }, [])

  const statusOptions = [
    {
      value: SHIFT_CONFLICTS,
      label: i18n.t('shift_management.filters.shift_conflicts'),
    },
    {
      value: SHIFT_WITHOUT_CONFLICTS,
      label: i18n.t('shift_management.filters.shift_without_conflicts'),
    },
  ]

  if (popoverOpen) {
    track(trackingEvents.shiftManagement.filterShiftManagementStart, {
      view: view.viewType,
      view_type_by: view.groupType,
    })
    setPopoverOpen(false)
  }

  // We use the general canManageShifts permission because we will display the disabled
  // button for planners that don't have coverage to promote upselling,
  // while still not showing it to regular employees.

  const showCoverageSectionButton = view.isByEmployees && view.isWeekly && canManageShifts

  // For Time Insights, depending on permissions a regular employee could
  // have access to it so we will handle the upselling option inside the component.
  const showTimeInsightsButton = view.isByEmployees

  return (
    <Box
      flexDirection="row"
      gap="s32"
      alignItems="center"
      minWidth="none"
      flexNoShrink
      flexGrow
      style={{ flexBasis: '0' }}
    >
      <Box flexDirection="row" gap="s8" alignItems="center">
        {newEntryFlowEnabled && (
          <Box minWidth="s200">
            <LocationSelector />
          </Box>
        )}
        {/* Custom CSS to reduce the paddingTop/paddingBottom, remove once the component is updated from Gamma */}
        <Box minWidth={newEntryFlowEnabled ? 's0' : 's80'} className={styles.filters}>
          {loading && <Skeleton width="s140" height="s40" />}
          {!loading && (
            <>
              {newEntryFlowEnabled ? (
                <FilterPopover
                  fields={fields}
                  filters={filters}
                  logicOperator={logicOperator}
                  onAddFilter={onAddFilter}
                  onRemoveFilter={onRemoveFilter}
                  onApplyFilters={onApplyFilters}
                  onCancel={onCancel}
                />
              ) : (
                <EmployeeMultiFilters
                  label={i18n.t('filters.multi_filters.title')}
                  fields={fields}
                  filters={filters}
                  logicOperator={logicOperator}
                  disabled={disabled}
                  onAddFilter={onAddFilter}
                  onRemoveFilter={onRemoveFilter}
                  onApplyFilters={onApplyFilters}
                  onCancel={onCancel}
                  onResetFilters={onResetFilters}
                  setChildPopoverOpen={setPopoverOpen}
                  dense
                />
              )}
            </>
          )}
        </Box>
        {newEntryFlowEnabled && <Separator />}
        <Box minWidth="s96">
          {newEntryFlowEnabled ? (
            !hasSeenNewEntryFlowHints ? (
              <Hint.Popover
                step={1}
                title={i18n.t('shift_management.hints.filters.title')}
                description={i18n.t('shift_management.hints.filters.description')}
                actionLabel={i18n.t('common.next')}
                position="bottom right"
              >
                <ViewOptionsSectionButton
                  disabled={hasNoneShiftManagementPlanning}
                  canManageShifts={canManageShifts}
                />
              </Hint.Popover>
            ) : (
              <ViewOptionsSectionButton
                disabled={hasNoneShiftManagementPlanning}
                canManageShifts={canManageShifts}
              />
            )
          ) : (
            <ViewSelectorOld disabled={!hasMonthlyView || disabled} />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default observer(CommandsFilterBar)
